import { useState } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Table,
  Button,
  Modal,
  Form,
  Select,
  Input,
  Switch,
  Space,
  Popconfirm,
  message,
  Spin,
  Empty,
  Collapse,
  DatePicker,
  TimePicker,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  useGetMachineQuery,
  useCreateMachineScheduleWeekMutation,
  useUpdateMachineScheduleWeekMutation,
  useDeleteMachineScheduleWeekMutation,
  useGetMachineDowntimesQuery,
  useCreateMachineDowntimeMutation,
  useDeleteMachineDowntimeMutation,
} from "../../../redux/api/scheduling";
import { toFacilityUTC, formatDateTime } from "../_shared/utils/dateUtils";

const DAYS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

interface Props {
  machineId: number;
}

export default function MachineDetail({ machineId }: Props) {
  const { data: machine, isLoading } = useGetMachineQuery(machineId);
  const { data: downtimes = [] } = useGetMachineDowntimesQuery(
    { machine: machineId },
    { skip: !machineId }
  );
  const dtList = Array.isArray(downtimes) ? downtimes : downtimes?.results || [];

  const [createScheduleWeek] = useCreateMachineScheduleWeekMutation();
  const [updateScheduleWeek] = useUpdateMachineScheduleWeekMutation();
  const [deleteScheduleWeek] = useDeleteMachineScheduleWeekMutation();
  const [createDowntime] = useCreateMachineDowntimeMutation();
  const [deleteDowntime] = useDeleteMachineDowntimeMutation();

  const [periodModalOpen, setPeriodModalOpen] = useState(false);
  const [editingPeriodWeekId, setEditingPeriodWeekId] = useState<number | null>(null);
  const [downtimeModalOpen, setDowntimeModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [downtimeForm] = Form.useForm();

  const weeks = machine?.schedule_weeks || [];
  const hasWeekA = weeks.some((w: any) => w.week_type === "A");
  const hasWeekB = weeks.some((w: any) => w.week_type === "B");

  const handleAddWeek = async (weekType: "A" | "B") => {
    try {
      await createScheduleWeek({
        machine: machineId,
        week_type: weekType,
        availability_periods: [
          {
            start_day: 0,
            start_time: "00:00:00",
            end_day: 6,
            end_time: "23:59:59",
            is_available: true,
            note: "24/7",
          },
        ],
      }).unwrap();
      message.success(`Week ${weekType} schedule created`);
    } catch (err: any) {
      message.error(err?.data?.detail || "Failed to create schedule week");
    }
  };

  const handleAddPeriod = (weekId: number) => {
    setEditingPeriodWeekId(weekId);
    form.resetFields();
    form.setFieldsValue({
      start_day: 1,
      start_time: dayjs("06:00", "HH:mm"),
      end_day: 5,
      end_time: dayjs("18:00", "HH:mm"),
      is_available: true,
      note: "",
    });
    setPeriodModalOpen(true);
  };

  const handleEditPeriod = (week: any, period: any) => {
    setEditingPeriodWeekId(week.id);
    form.setFieldsValue({
      start_day: period.start_day,
      start_time: period.start_time ? dayjs(period.start_time, "HH:mm:ss") : null,
      end_day: period.end_day,
      end_time: period.end_time ? dayjs(period.end_time, "HH:mm:ss") : null,
      is_available: period.is_available,
      note: period.note || "",
    });
    form.setFieldValue("_periodId", period.id);
    setPeriodModalOpen(true);
  };

  const handleSavePeriod = async (values: any) => {
    const week = weeks.find((w: any) => w.id === editingPeriodWeekId);
    if (!week) return;

    const periodData = {
      start_day: values.start_day,
      start_time: values.start_time ? values.start_time.format("HH:mm:ss") : "00:00:00",
      end_day: values.end_day,
      end_time: values.end_time ? values.end_time.format("HH:mm:ss") : "23:59:59",
      is_available: values.is_available,
      note: values.note || "",
    };

    const existingPeriods = (week.availability_periods || []).map((p: any) => ({
      start_day: p.start_day,
      start_time: typeof p.start_time === "string" ? p.start_time : "00:00:00",
      end_day: p.end_day,
      end_time: typeof p.end_time === "string" ? p.end_time : "23:59:59",
      is_available: p.is_available,
      note: p.note || "",
    }));

    const periodId = form.getFieldValue("_periodId");
    let newPeriods: any[];

    if (periodId) {
      newPeriods = existingPeriods.map((p: any, i: number) =>
        week.availability_periods?.[i]?.id === periodId ? periodData : p
      );
    } else {
      newPeriods = [...existingPeriods, periodData];
    }

    try {
      await updateScheduleWeek({
        id: week.id,
        availability_periods: newPeriods,
      }).unwrap();
      message.success("Availability period saved");
      setPeriodModalOpen(false);
      form.resetFields();
    } catch (err: any) {
      message.error(err?.data?.detail || "Failed to save period");
    }
  };

  const handleDeletePeriod = async (week: any, periodId: number) => {
    const existingPeriods = (week.availability_periods || []).filter(
      (p: any) => p.id !== periodId
    );
    const periodsData = existingPeriods.map((p: any) => ({
      start_day: p.start_day,
      start_time: typeof p.start_time === "string" ? p.start_time : "00:00:00",
      end_day: p.end_day,
      end_time: typeof p.end_time === "string" ? p.end_time : "23:59:59",
      is_available: p.is_available,
      note: p.note || "",
    }));

    try {
      await updateScheduleWeek({
        id: week.id,
        availability_periods: periodsData,
      }).unwrap();
      message.success("Period removed");
    } catch (err: any) {
      message.error(err?.data?.detail || "Failed to remove period");
    }
  };

  const handleAddDowntime = () => {
    downtimeForm.resetFields();
    downtimeForm.setFieldsValue({
      start_datetime: dayjs(),
      end_datetime: dayjs().add(1, "day"),
      reason: "",
    });
    setDowntimeModalOpen(true);
  };

  const handleSaveDowntime = async (values: any) => {
    try {
      await createDowntime({
        machine: machineId,
        start_datetime: toFacilityUTC(values.start_datetime),
        end_datetime: toFacilityUTC(values.end_datetime),
        reason: values.reason || "",
      }).unwrap();
      message.success("One-off downtime added");
      setDowntimeModalOpen(false);
      downtimeForm.resetFields();
    } catch (err: any) {
      message.error(err?.data?.detail || "Failed to add downtime");
    }
  };

  const handleDeleteDowntime = async (id: number) => {
    try {
      await deleteDowntime(id).unwrap();
      message.success("Downtime removed");
    } catch (err: any) {
      message.error(err?.data?.detail || "Failed to remove downtime");
    }
  };

  const handleDeleteWeek = async (weekId: number) => {
    try {
      await deleteScheduleWeek(weekId).unwrap();
      message.success("Schedule week removed");
    } catch (err: any) {
      message.error(err?.data?.detail || "Failed to remove schedule week");
    }
  };

  if (isLoading) return <Spin size="large" className="mt-10 mx-auto block" />;
  if (!machine) return <Empty description="Machine not found" />;

  return (
    <div>
      <Card className="mb-4">
        <Descriptions title={machine.name} bordered size="small" column={2}>
          <Descriptions.Item label="Attended Efficiency">
            {machine.attended_efficiency}%
          </Descriptions.Item>
          <Descriptions.Item label="Unattended Efficiency">
            {machine.unattended_efficiency}%
          </Descriptions.Item>
          <Descriptions.Item label="Max Unattended Hours">
            {machine.max_unattended_hours}
          </Descriptions.Item>
          <Descriptions.Item label="Active">
            {machine.is_active ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Weekly Schedule */}
      <Card
        title="Weekly Availability Schedule"
        className="mb-4"
        extra={
          <Space>
            {!hasWeekA && (
              <Button size="small" onClick={() => handleAddWeek("A")}>
                Add Week A
              </Button>
            )}
            {!hasWeekB && (
              <Button size="small" onClick={() => handleAddWeek("B")}>
                Add Week B
              </Button>
            )}
          </Space>
        }
      >
        <p className="text-sm text-gray-500 mb-3">
          Define recurring weekly availability. Use <Tag color="green">Available</Tag> for times
          the machine can run, and <Tag color="red">Down</Tag> for maintenance or blocked periods.
          Applied to future weeks like staff schedules.
        </p>
        {weeks.length === 0 ? (
          <Empty
            description="No schedule weeks. Add Week A or Week B to define availability."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Collapse>
            {weeks.map((week: any) => (
              <Collapse.Panel
                key={week.id}
                header={
                  <Space>
                    <span>Week {week.week_type}</span>
                    <Tag>{week.availability_periods?.length || 0} periods</Tag>
                  </Space>
                }
                extra={
                  <Space onClick={(e) => e.stopPropagation()}>
                    <Button
                      type="text"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => handleAddPeriod(week.id)}
                    >
                      Add period
                    </Button>
                    <Popconfirm
                      title="Remove this schedule week?"
                      onConfirm={() => handleDeleteWeek(week.id)}
                    >
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                }
              >
                {(week.availability_periods || []).length === 0 ? (
                  <Empty
                    description="No availability periods"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  >
                    <Button size="small" onClick={() => handleAddPeriod(week.id)}>
                      Add first period
                    </Button>
                  </Empty>
                ) : (
                  <Table
                    dataSource={week.availability_periods || []}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    columns={[
                      {
                        title: "Start",
                        key: "start",
                        render: (_: any, r: any) =>
                          `${DAYS.find((d) => d.value === r.start_day)?.label} ${String(r.start_time || "00:00").slice(0, 8)}`,
                      },
                      {
                        title: "End",
                        key: "end",
                        render: (_: any, r: any) =>
                          `${DAYS.find((d) => d.value === r.end_day)?.label} ${String(r.end_time || "23:59").slice(0, 8)}`,
                      },
                      {
                        title: "Status",
                        dataIndex: "is_available",
                        key: "is_available",
                        render: (v: boolean) =>
                          v ? (
                            <Tag color="green">Available</Tag>
                          ) : (
                            <Tag color="red">Down (maintenance)</Tag>
                          ),
                      },
                      { title: "Note", dataIndex: "note", key: "note" },
                      {
                        title: "Actions",
                        key: "actions",
                        render: (_: any, record: any) => (
                          <Space>
                            <Button
                              type="text"
                              size="small"
                              icon={<EditOutlined />}
                              onClick={() => handleEditPeriod(week, record)}
                            />
                            <Popconfirm
                              title="Remove this period?"
                              onConfirm={() => handleDeletePeriod(week, record.id)}
                            >
                              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                            </Popconfirm>
                          </Space>
                        ),
                      },
                    ]}
                  />
                )}
              </Collapse.Panel>
            ))}
          </Collapse>
        )}
      </Card>

      {/* One-off Downtime */}
      <Card
        title="One-off Downtime"
        className="mb-4"
        extra={
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={handleAddDowntime}>
            Add downtime
          </Button>
        }
      >
        <p className="text-sm text-gray-500 mb-3">
          Schedule specific calendar dates when the machine is unavailable (e.g. annual maintenance,
          repairs). These override the weekly schedule for those dates.
        </p>
        {dtList.length === 0 ? (
          <Empty
            description="No one-off downtimes scheduled"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Table
            dataSource={dtList}
            rowKey="id"
            size="small"
            pagination={false}
            columns={[
              {
                title: "Start",
                dataIndex: "start_datetime",
                key: "start_datetime",
                render: (v: string) => (v ? formatDateTime(v, "YYYY-MM-DD HH:mm") : "-"),
              },
              {
                title: "End",
                dataIndex: "end_datetime",
                key: "end_datetime",
                render: (v: string) => (v ? formatDateTime(v, "YYYY-MM-DD HH:mm") : "-"),
              },
              { title: "Reason", dataIndex: "reason", key: "reason" },
              {
                title: "Actions",
                key: "actions",
                render: (_: any, record: any) => (
                  <Popconfirm
                    title="Remove this downtime?"
                    onConfirm={() => handleDeleteDowntime(record.id)}
                  >
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                ),
              },
            ]}
          />
        )}
      </Card>

      {/* Period modal */}
      <Modal
        title="Availability Period"
        open={periodModalOpen}
        onCancel={() => setPeriodModalOpen(false)}
        onOk={() => form.submit()}
        width={480}
      >
        <Form form={form} layout="vertical" onFinish={handleSavePeriod}>
          <Form.Item name="_periodId" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="start_day" label="Start Day" rules={[{ required: true }]}>
            <Select options={DAYS} />
          </Form.Item>
          <Form.Item name="start_time" label="Start Time">
            <TimePicker format="HH:mm" className="w-full" />
          </Form.Item>
          <Form.Item name="end_day" label="End Day" rules={[{ required: true }]}>
            <Select options={DAYS} />
          </Form.Item>
          <Form.Item name="end_time" label="End Time">
            <TimePicker format="HH:mm" className="w-full" />
          </Form.Item>
          <Form.Item name="is_available" label="Available" valuePropName="checked">
            <Switch checkedChildren="Available" unCheckedChildren="Down (maintenance)" />
          </Form.Item>
          <Form.Item name="note" label="Note">
            <Input placeholder="e.g. Weekly maintenance" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Downtime modal */}
      <Modal
        title="Add One-off Downtime"
        open={downtimeModalOpen}
        onCancel={() => setDowntimeModalOpen(false)}
        onOk={() => downtimeForm.submit()}
        width={480}
      >
        <Form form={downtimeForm} layout="vertical" onFinish={handleSaveDowntime}>
          <Form.Item name="start_datetime" label="Start" rules={[{ required: true }]}>
            <DatePicker showTime className="w-full" />
          </Form.Item>
          <Form.Item name="end_datetime" label="End" rules={[{ required: true }]}>
            <DatePicker showTime className="w-full" />
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <Input placeholder="e.g. Annual maintenance" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
